// Projects.jsx - Garanta que NÃO tem app_status
import Card from "../projects_cards";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MainContainer, ContentWrapper, ProjectListWrapper } from "./styles";
import { storeProjects } from "../../store/modules/app_data/actions";
import { useNavigate } from "react-router-dom";
import { projectService } from "../../services/projectService";
import { blockchainService } from "../../services/blockchainService";
import PageHeader from "../page_header";
import {
  isPropOrGuardianUser,
  isAdminOrEngineeringUser,
} from "../../utils/userRoles";

const PROJECT_SEARCH_COLUMNS = [
  { value: "Nome", label: "Nome - Título do Projeto" },
  { value: "Unidade de Conservação (UC)", label: "Unidade de Conservação (UC)" },
  { value: "Localidade", label: "Localidade" },
  { value: "Status da Matrícula", label: "Status da Matrícula" },
  { value: "Status do Georreferenciamento", label: "Status do Georreferenciamento" },
  { value: "Situação da Reserva Legal", label: "Situação da Reserva Legal" },
  { value: "Status CAR", label: "Status CAR" },
];

const Projects = () => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const [selectedColumn, setSelectedColumn] = useState("Nome");
  const [searchValue, setSearchValue] = useState("");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [documentModels, setDocumentModels] = ("");
  const canAddProject = !isPropOrGuardianUser(currentUser);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await blockchainService.getDocumentModels();
        dispatch(setDocumentModels(response.data));
      } catch (error) {
        console.error("Erro ao carregar modelos:", error);
      }
    };

    fetchModels();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      try {
        setLoading(true);
        let response;

        if (isAdminOrEngineeringUser(currentUser)) {
          response = await projectService.getProjects();
        } else {
          response = await projectService.getProjectByUserId(currentUser.id);
        }

        const projectsArray = response?.projects_project || response || [];
        setProjects(Array.isArray(projectsArray) ? projectsArray : []);
        hasLoadedRef.current = true;

        if (Array.isArray(projectsArray)) {
          dispatch(storeProjects(projectsArray));
        }
      } catch (error) {
        console.error("❌ [Projects] Erro ao buscar projetos:", error);
        setProjects([]);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    if (currentUser?.id) {
      fetchProjects();
    }
  }, [currentUser.id, currentUser.user_type]);

  const filteredProjects = useMemo(() => {
    const result = projects.filter((project) => {
      if (!searchValue) return true;
      if (!project) return false;

      const searchTerm = searchValue.toLowerCase();

      switch (selectedColumn) {
        case "Nome":
          return project.title?.toLowerCase().includes(searchTerm) || false;
        case "Localidade":
          return project.address?.toLowerCase().includes(searchTerm) || false;
        case "Unidade de Conservação (UC)":
          return (
            project.conservation_unit?.toLowerCase().includes(searchTerm) || false
          );
        case "Status da Matrícula":
          if (project.guardian_areas && Array.isArray(project.guardian_areas)) {
            return project.guardian_areas.some((area) =>
              area.matricula_status?.toLowerCase().includes(searchTerm)
            );
          }
          return false;
        case "Status do Georreferenciamento":
          if (project.guardian_areas && Array.isArray(project.guardian_areas)) {
            return project.guardian_areas.some((area) =>
              area.georeferencing_status?.toLowerCase().includes(searchTerm)
            );
          }
          return false;
        case "Situação da Reserva Legal":
          if (project.guardian_areas && Array.isArray(project.guardian_areas)) {
            return project.guardian_areas.some((area) =>
              area.reserve_legal_status?.toLowerCase().includes(searchTerm)
            );
          }
          return false;
        case "Status CAR":
          if (project.guardian_areas && Array.isArray(project.guardian_areas)) {
            return project.guardian_areas.some((area) =>
              area.status_car?.toLowerCase().includes(searchTerm)
            );
          }
          return false;
        default:
          return true;
      }
    });

    return result;
  }, [projects, searchValue, selectedColumn]);

  const handleClick = useCallback(() => {
    navigate("/project_register");
  }, [navigate]);

  if (loading) {
    return (
      <MainContainer>
        <ContentWrapper>
          <div>Carregando projetos...</div>
        </ContentWrapper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ContentWrapper>
        <PageHeader
          title="Projetos"
          actionLabel={canAddProject ? "Adicionar Projeto" : undefined}
          onAction={canAddProject ? handleClick : undefined}
          search={{
            columns: PROJECT_SEARCH_COLUMNS,
            selectedColumn,
            onColumnChange: (e) => setSelectedColumn(e.target.value),
            searchValue,
            onSearchChange: (e) => setSearchValue(e.target.value),
          }}
        />

        <ProjectListWrapper>
          {projects.length === 0 ? (
            <div>Nenhum projeto encontrado.</div>
          ) : (
            <Card filteredProjects={filteredProjects} />
          )}
        </ProjectListWrapper>
      </ContentWrapper>
    </MainContainer>
  );
};

export default Projects;
